import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Tabs,
    FormField,
    Select,
    Grid,
    Box,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState } from 'react';
import { fetchTestRuns } from './apis';
import * as Constants from './constants';
import TestRunsTable from './TestRunsTable';
import { getTestResult, getTimeoutInMin, getTestTypeName, getTestSuite } from './utils';
import { getCurrentAuthUserAlias, getCurrentAuthUserEmail, isValidInput } from '../../lib/utils';
import { listAllBots } from '../../api-auth/bot-accounts/apis';
import { RegionContext } from '../../CustomContext';

export default function Testruns() {
    const [loadingByRequester, setLoadingByRequester] = React.useState(false);
    const [loadingByGroup, setLoadingByGroup] = React.useState(false);
    const [testrunsByReqester, setTestrunsByReqester] = React.useState([]);
    const [testrunsByGroup, setTestrunsByGroup] = React.useState([]);
    const [defaultUser, setDefaultUser] = React.useState('');
    const [usersStatus, setUsersStatus] = React.useState('pending');
    const [usersSelectedOption, setUsersSelectedOption] = React.useState({});
    const [usersOptions, setUsersOptions] = React.useState([]);
    const [userSelectionDisabled, setUserSelectionDisabled] = React.useState(true);
    const [selectedUserEmail, setSelectedUserEmail] = React.useState('');
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);

    /**
     * set initial status
     */
    const setInitialStatus = () => {
        (async function () {
            setUserSelectionDisabled(true);
            const email = await getCurrentAuthUserEmail();
            await Promise.all([loadUsers(), loadTableByRequester(email), loadTableByGroup(email)]);
            setUserSelectionDisabled(false);
        })();
    };

    /**
     * load users
     */
    const loadUsers = async () => {
        setUsersStatus('loading');
        try {
            var resultOptions = [];
            // set default user
            const alias = await getCurrentAuthUserAlias();
            const email = await getCurrentAuthUserEmail();
            setDefaultUser(alias);
            setSelectedUserEmail(email);
            setUsersSelectedOption({
                label: alias,
                value: alias,
            });
            resultOptions.push({
                label: 'Default view',
                options: [{ label: alias, value: alias }],
            });

            // loading bot account user
            var botOptions = [];
            const response = await listAllBots();
            const bots = response['response'];
            bots.forEach((bot) => {
                if (isValidInput(bot.username)) {
                    botOptions.push({ label: bot.username, value: bot.username });
                }
            });
            resultOptions.push({
                label: 'Bot account users',
                options: botOptions,
            });
            setUsersOptions(resultOptions);
            setUsersStatus('finished');
        } catch (e) {
            console.error('Fail to load user(s) with error', e);
            setUsersStatus('error');
        }
    };

    /**
     * process on user selection change event
     * @param {*} selectedOption
     */
    const onUsersSelectionChange = (selectedOption) => {
        (async function () {
            setUserSelectionDisabled(true);
            setUsersSelectedOption({
                label: selectedOption.label,
                value: selectedOption.value,
            });
            if (selectedOption.value === defaultUser) {
                const email = await getCurrentAuthUserEmail();
                setSelectedUserEmail(email);
                await Promise.all([loadTableByRequester(email), loadTableByGroup(email)]);
            } else {
                const email = `${selectedOption.value}@lasso.labcollab.net`;
                setSelectedUserEmail(email);
                await Promise.all([loadTableByRequester(email), loadTableByGroup(email)]);
            }
            setUserSelectionDisabled(false);
        })();
    };

    /**
     * post process testruns
     * @param {*} response
     * @returns
     */
    const postProcessTestRuns = (response) => {
        let testruns = response['response'];
        for (let index = 0; index < testruns.length; index++) {
            let testrun = testruns[index];
            testrun[Constants.TEST_RESULT] = getTestResult(testrun);
            testrun[Constants.TEST_TIMEOUT] = getTimeoutInMin(testrun[Constants.TEST_TIMEOUT]);
            testrun[Constants.TEST_RUN_TYPE_NAME] = getTestTypeName(testrun[Constants.TEST_RUN_TYPE]);
            testrun[Constants.TEST_SUITE] = getTestSuite(testrun);

            const buildConfig = testrun[Constants.TEST_BUILD] || {};
            testrun[Constants.BUILD_FIREOS] = buildConfig[Constants.BUILD_FIREOS] || '';
            testrun[Constants.BUILD_PROJECT] = buildConfig[Constants.BUILD_PROJECT] || '';
            testrun[Constants.BUILD_TYPE] = buildConfig[Constants.BUILD_TYPE] || '';
            testrun[Constants.BUILD_VARIANT] = buildConfig[Constants.BUILD_VARIANT] || '';
            testrun[Constants.BUILD_VERSION] = buildConfig[Constants.BUILD_VERSION] || '';
        }
        return testruns;
    };

    /**
     * load test run table by requester
     * @param {*} email
     */
    const loadTableByRequester = async (email) => {
        setLoadingByRequester(true);
        try {
            const response = await fetchTestRuns(email, true, selectedRegion.value);
            let testruns = postProcessTestRuns(response);
            setTestrunsByReqester(testruns);
        } catch (e) {
            console.error('Fail to load test run(s) by requester with error', e);
        }
        setLoadingByRequester(false);
    };

    /**
     * load test run table by group
     * @param {*} email
     */
    const loadTableByGroup = async (email) => {
        setLoadingByGroup(true);
        try {
            const response = await fetchTestRuns(email, false, selectedRegion.value);
            let testruns = postProcessTestRuns(response);
            setTestrunsByGroup(testruns);
        } catch (e) {
            console.error('Fail to load test run(s) by groups with error', e);
        }
        setLoadingByGroup(false);
    };

    /**
     * user refresh table
     */
    const refresh = () => {
        (async function () {
            setUserSelectionDisabled(true);
            await Promise.all([loadTableByRequester(selectedUserEmail), loadTableByGroup(selectedUserEmail)]);
            setUserSelectionDisabled(false);
        })();
    };

    useEffect(() => {
        // initialize the component
        setInitialStatus();
    }, []);
    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'Test runs', href: '#/automation/test-runs' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description="This page shows all automation test runs that you are allowed to view."
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Select
                            inlineLabelText="Viewing as:"
                            selectedOption={usersSelectedOption}
                            expandToViewport
                            loadingText="Loading users"
                            errorText="Error fetching users"
                            finishedText="End of all results"
                            empty="No user found"
                            filteringType="auto"
                            virtualScroll={true}
                            statusType={usersStatus}
                            options={usersOptions}
                            onChange={({ detail }) => onUsersSelectionChange(detail.selectedOption)}
                            disabled={userSelectionDisabled}
                        />
                    </SpaceBetween>
                }
            >
                Welcome to Amazon Device Farm - Test runs
            </Header>
            <Tabs
                tabs={[
                    {
                        label: 'As requester',
                        id: 'first',
                        content: TestRunsTable(
                            testrunsByReqester,
                            Constants.TESTRUN_COLUMNS_DEFINITION,
                            Constants.VISIBLE_COLUMNS_AS_REQUESTER,
                            loadingByRequester,
                            refresh
                        ),
                    },
                    {
                        label: 'All test runs',
                        id: 'second',
                        content: TestRunsTable(
                            testrunsByGroup,
                            Constants.TESTRUN_COLUMNS_DEFINITION,
                            Constants.VISIBLE_COLUMNS_ALL,
                            loadingByGroup,
                            refresh
                        ),
                    },
                ]}
            />
        </SpaceBetween>
    );
}
